'use client';

import { useState } from 'react';
import { CareerPath } from '@/lib/careerPaths';
import {
  getInterviewPrep,
  getCategoryLabel,
  getDifficultyInfo,
  InterviewQuestion,
} from '@/lib/interviewPrepData';
import styles from './InterviewPrep.module.css';

interface InterviewPrepProps {
  career: CareerPath;
}

type TabId = 'questions' | 'star' | 'ask' | 'tips';

export default function InterviewPrep({ career }: InterviewPrepProps) {
  const [activeTab, setActiveTab] = useState<TabId>('questions');
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const prepData = getInterviewPrep(career);

  const handleCopy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const tabs: { id: TabId; label: string; icon: string }[] = [
    { id: 'questions', label: 'Common Questions', icon: '❓' },
    { id: 'star', label: 'STAR Examples', icon: '⭐' },
    { id: 'ask', label: 'Questions to Ask', icon: '🙋' },
    { id: 'tips', label: 'Prep Tips', icon: '💡' },
  ];

  const renderQuestionCard = (question: InterviewQuestion, index: number) => {
    const isExpanded = expandedQuestion === index;
    const difficultyInfo = getDifficultyInfo(question.difficulty);

    return (
      <div
        key={index}
        className={`${styles.questionCard} ${isExpanded ? styles.expanded : ''}`}
      >
        <button
          className={styles.questionHeader}
          onClick={() => setExpandedQuestion(isExpanded ? null : index)}
        >
          <div className={styles.questionMeta}>
            <span
              className={styles.categoryBadge}
              data-category={question.category}
            >
              {getCategoryLabel(question.category)}
            </span>
            <span
              className={styles.difficultyBadge}
              style={{ backgroundColor: `${difficultyInfo.color}20`, color: difficultyInfo.color }}
            >
              {difficultyInfo.label}
            </span>
          </div>
          <h4 className={styles.questionText}>{question.question}</h4>
          <span className={styles.expandIcon}>{isExpanded ? '−' : '+'}</span>
        </button>

        {isExpanded && (
          <div className={styles.questionDetails}>
            <div className={styles.sampleAnswer}>
              <div className={styles.sampleAnswerHeader}>
                <h5>Sample Answer</h5>
                <button
                  className={styles.copyButton}
                  onClick={() => handleCopy(question.sampleAnswer, index)}
                >
                  {copiedIndex === index ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <p>{question.sampleAnswer}</p>
            </div>

            <div className={styles.tipsSection}>
              <h5>Tips for Success</h5>
              <ul className={styles.tipsList}>
                {question.tips.map((tip, i) => (
                  <li key={i} className={styles.tipItem}>
                    <span className={styles.tipIcon}>✓</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.redFlagsSection}>
              <h5>Avoid These Red Flags</h5>
              <ul className={styles.redFlagsList}>
                {question.redFlags.map((flag, i) => (
                  <li key={i} className={styles.redFlagItem}>
                    <span className={styles.redFlagIcon}>⚠️</span>
                    {flag}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Interview Preparation</h3>
        <p className={styles.subtitle}>
          Master the interview for {career.title} positions
        </p>
      </div>

      <div className={styles.tabNav}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className={styles.content}>
        {activeTab === 'questions' && (
          <div className={styles.questionsTab}>
            <p className={styles.tabIntro}>
              Practice these common interview questions for {career.title} roles.
              Click on any question to see a sample answer and tips.
            </p>
            <div className={styles.questionsList}>
              {prepData.commonQuestions.map((q, i) => renderQuestionCard(q, i))}
            </div>
          </div>
        )}

        {activeTab === 'star' && (
          <div className={styles.starTab}>
            <div className={styles.starIntro}>
              <h4>The STAR Method</h4>
              <p>
                Structure your behavioral answers using <strong>Situation</strong>,{' '}
                <strong>Task</strong>, <strong>Action</strong>, <strong>Result</strong>.
              </p>
              <div className={styles.starBreakdown}>
                <div className={styles.starStep}>
                  <span className={styles.starLetter}>S</span>
                  <div>
                    <strong>Situation</strong>
                    <p>Set the scene with context</p>
                  </div>
                </div>
                <div className={styles.starStep}>
                  <span className={styles.starLetter}>T</span>
                  <div>
                    <strong>Task</strong>
                    <p>Describe your responsibility</p>
                  </div>
                </div>
                <div className={styles.starStep}>
                  <span className={styles.starLetter}>A</span>
                  <div>
                    <strong>Action</strong>
                    <p>Explain what you did</p>
                  </div>
                </div>
                <div className={styles.starStep}>
                  <span className={styles.starLetter}>R</span>
                  <div>
                    <strong>Result</strong>
                    <p>Share the outcome with metrics</p>
                  </div>
                </div>
              </div>
            </div>

            <h4 className={styles.examplesHeader}>Example STAR Stories</h4>
            <div className={styles.starExamples}>
              {prepData.starExamples.map((example, i) => (
                <div key={i} className={styles.starExample}>
                  <div className={styles.starRow}>
                    <span className={styles.starLabel}>Situation</span>
                    <p>{example.situation}</p>
                  </div>
                  <div className={styles.starRow}>
                    <span className={styles.starLabel}>Task</span>
                    <p>{example.task}</p>
                  </div>
                  <div className={styles.starRow}>
                    <span className={styles.starLabel}>Action</span>
                    <p>{example.action}</p>
                  </div>
                  <div className={styles.starRow}>
                    <span className={styles.starLabel}>Result</span>
                    <p>{example.result}</p>
                  </div>
                  <button
                    className={styles.copyStarButton}
                    onClick={() => handleCopy(
                      `Situation: ${example.situation}\n\nTask: ${example.task}\n\nAction: ${example.action}\n\nResult: ${example.result}`,
                      100 + i
                    )}
                  >
                    {copiedIndex === 100 + i ? '✓ Copied' : 'Copy Story'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'ask' && (
          <div className={styles.askTab}>
            <p className={styles.tabIntro}>
              Always prepare thoughtful questions to ask your interviewers.
              It shows genuine interest and helps you evaluate the opportunity.
            </p>
            <div className={styles.questionCategories}>
              {(['role', 'team', 'growth', 'culture'] as const).map((category) => {
                const categoryQuestions = prepData.questionsToAsk.filter(q => q.category === category);
                if (categoryQuestions.length === 0) return null;

                const categoryLabels = {
                  role: { label: 'About the Role', icon: '💼' },
                  team: { label: 'About the Team', icon: '👥' },
                  growth: { label: 'Growth & Development', icon: '📈' },
                  culture: { label: 'Company Culture', icon: '🏢' }
                };

                return (
                  <div key={category} className={styles.questionCategory}>
                    <h4>
                      <span>{categoryLabels[category].icon}</span>
                      {categoryLabels[category].label}
                    </h4>
                    {categoryQuestions.map((q, i) => (
                      <div key={i} className={styles.questionToAsk}>
                        <p className={styles.askQuestion}>&ldquo;{q.question}&rdquo;</p>
                        <p className={styles.askWhy}>
                          <strong>Why ask:</strong> {q.why}
                        </p>
                        <button
                          className={styles.copyQuestionButton}
                          onClick={() => handleCopy(q.question, 200 + categoryQuestions.indexOf(q))}
                        >
                          {copiedIndex === 200 + categoryQuestions.indexOf(q) ? '✓' : 'Copy'}
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'tips' && (
          <div className={styles.tipsTab}>
            <div className={styles.tipSection}>
              <h4>
                <span className={styles.tipSectionIcon}>👔</span>
                Dress Code
              </h4>
              <p>{prepData.dresscode}</p>
            </div>

            <div className={styles.tipSection}>
              <h4>
                <span className={styles.tipSectionIcon}>📋</span>
                Common Interview Formats
              </h4>
              <ul className={styles.formatList}>
                {prepData.interviewFormats.map((format, i) => (
                  <li key={i}>{format}</li>
                ))}
              </ul>
            </div>

            <div className={styles.tipSection}>
              <h4>
                <span className={styles.tipSectionIcon}>🚫</span>
                Common Mistakes to Avoid
              </h4>
              <ul className={styles.mistakesList}>
                {prepData.commonMistakes.map((mistake, i) => (
                  <li key={i}>
                    <span className={styles.xIcon}>✗</span>
                    {mistake}
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.tipSection}>
              <h4>
                <span className={styles.tipSectionIcon}>📅</span>
                The Day Before Your Interview
              </h4>
              <ol className={styles.dayBeforeList}>
                {prepData.dayBeforeTips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
